import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Analytics } from '@vercel/analytics/react';
import type { User } from './types';
import { authService } from './services/auth';
import { loadAvatarOverrides } from './lib/wrapped/avatarUtils';
import { AuthProvider } from './contexts/AuthContext';
import { WingoBalanceProvider } from './contexts/WingoBalanceContext';
import Navbar from './components/navbar';
import Footer from './components/Footer';
import Home from './pages/home';
import Login from './pages/login';
import Register from './components/register';
import { ForgotPassword } from './components/forgot-password';
import ResetPassword from './components/reset-password';
import Wallet from './components/wallet';
import MineWingo from './pages/minewingo';
import Ledger from './pages/ledger';
import Wager from './pages/wager';
import CreateBet from './pages/createbet';
import BetBoard from './pages/betboard';
import WingateInvitational from './pages/wingateinvitational';
import FAQ from './pages/raq';
import Experience from './pages/experience';
import WingoConverter from './components/WingoConverter';
import VDOTTimes from './pages/vdot-times';
import WingoWednesday from './pages/wingo-wednesday';
import Vote from './pages/vote';
import DaisyMath from './pages/daisy_math';
import DaisyWiki from './pages/daisy-wiki';
import WingoWiki from './pages/wingo-wiki';
import WingoUnitWiki from './pages/wingo-unit-wiki';
import WingateWiki from './pages/wingate-wiki';
import OldRileyWiki from './pages/old-riley-wiki';
import OldBalance from './components/old_balance';
import WingoLog from './pages/wingo-log';
import Wingonomics from './pages/wingonomics';
import LaneConverter from './components/LaneConverter';
import TestModal from './pages/test-modal';
import Maths from './pages/maths';
import AvatarTest from './pages/avatar-test';
import ObwiResults from './pages/obwi-results';
import Wapped from './pages/wapped';
import Authorize from './pages/authorize';
import Challenge from './pages/challenge';
import ChallengeEntry from './pages/challenge-entry';
import Support from './pages/Support';
import Privacy from './pages/privacy';
import WingoImperialingo from './pages/wingo-imperialingo';
import Clipboard from './pages/clipboard';
import AdminUsers from './pages/admin/users';

const queryClient = new QueryClient();

// Redirect component for old /wrapped route
const WrappedRouteRedirect: React.FC = () => {
  const { username } = useParams<{ username?: string }>();
  return <Navigate to={username ? `/wapped/${username}` : '/wapped'} replace />;
};

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Track page view for Vercel Analytics (React Router SPA)
    if (typeof window !== 'undefined' && (window as any).va) {
      (window as any).va('track', { event: 'pageview', path: location.pathname });
    }
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const isWrappedRoute = location.pathname.startsWith('/wapped') || location.pathname.startsWith('/wrapped');
  const isClipboardRoute = location.pathname === '/clipboard';
  const isAdminUsersRoute = location.pathname === '/admin/users';
  const isMinimalChrome = isWrappedRoute || isClipboardRoute || isAdminUsersRoute;

  useEffect(() => {
    void loadAvatarOverrides();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authService.getCurrentUser()
        .then((userData: User) => {
          setUser(userData);
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const showNavAndFooter = !isMinimalChrome;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gray-50">Loading...</div>;
  }

  return (
    <>
      <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WingoBalanceProvider>
          <div className={`min-h-screen ${isWrappedRoute ? 'bg-black' : (isClipboardRoute || isAdminUsersRoute) ? 'bg-stone-100' : 'bg-gray-50'} flex flex-col`}>
            {showNavAndFooter && <Navbar user={user} onLogout={handleLogout} />}
            <main className={isMinimalChrome ? 'flex-grow' : 'container mx-auto px-4 py-8 flex-grow'}>
              <Routes>
                <Route path="/" element={<Ledger user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} />} />
                <Route path="/login" element={!user ? <Login onLogin={setUser} /> : <Navigate to="/" />} />
                <Route path="/register" element={!user ? <Register onRegister={setUser} /> : <Navigate to="/" />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/wallet" element={<Wallet user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} />} />
                <Route path="/mine" element={<MineWingo user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} onMineWingo={(amount) => {
                  if (user) {
                    setUser(prev => prev ? { ...prev, wingo_balance: prev.wingo_balance + amount } : null);
                  }
                }} />} />
                <Route path="/ledger" element={<Ledger user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} />} />
                <Route path="/wager" element={<Wager user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} />} />
                <Route path="/vote" element={user ? <Vote /> : <Navigate to="/login" />} />
                <Route path="/create-bet" element={<CreateBet user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} onCreateBet={() => {}} />} />
                <Route path="/bet-board" element={user ? <BetBoard user={user} bets={[]} onAcceptBet={() => {}} /> : <Navigate to="/login" />} />
                <Route path="/wingate-invitational" element={<WingateInvitational user={user || {
                  id: 'guest',
                  email: '',
                  username: 'Guest',
                  name: 'Guest User',
                  wingo_balance: 0,
                  total_wingos: 0,
                  created_at: new Date().toISOString(),
                  last_activity: new Date().toISOString(),
                  balance: 0,
                  isActive: true,
                  createdAt: new Date()
                }} />} />
                <Route path="/obwi" element={<Navigate to="/obwi-results" replace />} />
                <Route path="/experience" element={<Experience />} />
                <Route path="/wingo-wednesday" element={<WingoWednesday />} />
                <Route path="/raq" element={<FAQ />} />
                <Route path="/converter" element={<WingoConverter />} />
                <Route path="/vdot-paces" element={<VDOTTimes initialView="pace" user={user} />} />
                <Route path="/vdot" element={<VDOTTimes initialView="pace" user={user} />} />
                <Route path="/dvot" element={<VDOTTimes initialView="pace" user={user} />} />
                <Route path="/319" element={<WingoImperialingo />} />
                <Route path="/vdot-times" element={<VDOTTimes initialView="pace" user={user} />} />
                <Route path="/wingipedia" element={<DaisyMath />} />
                <Route path="/daisy" element={<DaisyWiki />} />
                <Route path="/wingipedia/daisy" element={<DaisyWiki />} />
                <Route path="/wingipedia/wingo" element={<WingoWiki />} />
                <Route path="/wingipedia/wingo-unit" element={<WingoUnitWiki />} />
                <Route path="/wingipedia/wingate" element={<WingateWiki />} />
                <Route path="/wingipedia/old-riley" element={<OldRileyWiki />} />
                <Route path="/daisy_math" element={<DaisyMath />} />
                <Route path="/maths" element={<Navigate to="/wingipedia" replace />} />
                <Route path="/dictionary" element={<Navigate to="/wingipedia" replace />} />
                <Route path="/glossary" element={<Navigate to="/wingipedia" replace />} />
                <Route path="/old-balance" element={<OldBalance />} />
                <Route path="/wlog" element={<WingoLog />} />
                <Route path="/wingonomics" element={<Wingonomics />} />
                <Route path="/lanes" element={<LaneConverter />} />
                <Route path="/test-modal" element={<TestModal />} />
                <Route path="/avatar-test" element={<AvatarTest />} />
                <Route path="/obwi-results" element={<ObwiResults />} />
                <Route path="/initiation" element={<Authorize />} />
                <Route path="/support" element={<Support />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/wapped/:username?" element={<Wapped />} />
                <Route path="/wrapped/:username?" element={<WrappedRouteRedirect />} />
                <Route path="/challenge" element={<ChallengeEntry />} />
                <Route path="/challenge/:username" element={<Challenge />} />
                <Route path="/clipboard" element={<Clipboard />} />
                <Route path="/admin/users" element={<AdminUsers />} />
              </Routes>
            </main>
            {showNavAndFooter && <Footer />}
          </div>
          <Analytics />
        </WingoBalanceProvider>
      </AuthProvider>
      </QueryClientProvider>
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <AppContent />
    </Router>
  );
}

export default App; 